import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { deleteFile } from 'services/compose'
import { StoreThunkDispatch } from 'store/state'

/**
 * Queue for deleting uploaded composer files. Returns a callback that takes the url of an upload
 * that is no longer needed; failures are logged and swallowed, since a file we cannot delete must
 * not break the composer. The queue is torn down with the component that owns it.
 */
export function useDeleteFileQueue() {
  const storeDispatch = useDispatch<StoreThunkDispatch>()
  const delete$ = useRef<Subject<string>>(undefined)
  // EXPL: child effects run before the effects of the component that owns the queue, so a delete
  // requested while mounting arrives before there is anything subscribed to hand it to.
  const pending = useRef<string[]>([])

  useEffect(() => {
    const subject = new Subject<string>()
    const subscription = subject
      .pipe(
        mergeMap((url: string) => storeDispatch(deleteFile(url)).pipe(
          catchError(error => {
            console.log('[ComposerUploader] error deleting file', error)
            return observableOf({})
          })
        ))
      )
      .subscribe()

    delete$.current = subject
    pending.current.splice(0).forEach(url => subject.next(url))

    return () => {
      // NOTE: the subscription, not the subject — unsubscribing the subject leaves the pipe running
      // and makes every later call throw ObjectUnsubscribedError at the caller.
      delete$.current = undefined
      subscription.unsubscribe()
    }
  }, [storeDispatch])

  return useCallback((url?: string) => {
    if (!url) {
      return
    }
    if (delete$.current) {
      delete$.current.next(url)
      return
    }
    pending.current.push(url)
  }, [])
}
